/**
 * 同步服务
 * 处理本地数据和服务端数据的同步
 * 
 * 接口说明：
 * POST /navs/sync
 * 入参格式：
 * [
 *   {
 *     id: string,          // 导航ID
 *     name: string,        // 导航名称
 *     icon: string,        // 导航图标
 *     websites: [          // 导航下的网站列表
 *       {
 *         id: string,      // 网站ID
 *         title: string,   // 网站标题
 *         url: string,     // 网站地址
 *         ico: string,     // 网站图标
 *         bgColor: string  // 背景色
 *       }
 *     ]
 *   }
 * ]
 * 
 * 返回格式：与入参格式相同，包含服务端合并后的完整数据
 * 
 * 功能流程：
 * 1. 整合本地菜单和网站数据为统一格式
 * 2. 发送到后端进行数据合并
 * 3. 解析合并后的数据并更新本地状态
 * 4. 处理同步过程中的错误情况
 */

import { setNavItems } from '../redux/slices/navSlice';
import { setAllWebsites } from '../redux/slices/websiteSlice';
import logger from '../utils/logger';
import { post } from './apiClient';
import { AUTH_TOKEN_KEY,USER_INFO_KEY } from '../services/authService';
import { getAuthDataFromStorage } from './authService';
import storage from '../utils/LocalStorageManager';

const syncLogger = logger.withPrefix('同步服务');

// 获取store的函数
const getStore = () => {
  return require('../redux/store').default;
};

/**
 * 检查用户是否已登录并获取 token
 * @returns {Object|null} 包含 token 的对象或 null
 */
const getAuthToken = () => {
  const authData = getAuthDataFromStorage();
  return authData && authData.token ? { token: authData.token } : null;
};

/**
 * 调用后端同步接口
 * @param {Array} data 本地数据，格式与 defaultNavItems 相同
 * @returns {Promise<Array>} 合并后的数据
 */
const callSyncApi = async (data) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('未获取到认证 token');
    }

    const response = await post('/navs/sync', {navs:data}, token);
    return response.data;
  } catch (error) {
    syncLogger.error('调用同步接口失败:', error);
    throw error;
  }
};

/**
 * 整合本地菜单和网站数据为 defaultNavItems 格式
 * @param {Array} navItems 导航菜单数据
 * @param {Object} websitesByNav 网站数据
 * @returns {Array} 整合后的数据
 */
const integrateLocalData = (navItems, websitesByNav) => {
  return navItems.map(nav => ({
    ...nav,
    websites: websitesByNav[nav.id] || []
  }));
};

/**
 * 从合并后的数据中分离出菜单和网站数据
 * @param {Array} mergedData 合并后的数据
 * @returns {{navItems: Array, websitesByNav: Object}} 分离后的数据
 */
const separateMergedData = (mergedData) => {
  const navItems = mergedData.map(({ websites, ...nav }) => nav);
  const websitesByNav = {};
  
  mergedData.forEach(nav => {
    if (Array.isArray(nav.websites)) {
      websitesByNav[nav.id] = nav.websites;
    }
  });

  return { navItems, websitesByNav };
};

/**
 * 同步所有本地数据到服务端并获取合并后的数据
 * @param {Function} dispatch Redux dispatch 函数
 * @returns {Promise<{success: boolean, message: string}>} 同步结果和消息
 */
export const syncAllDataToServer = async (dispatch) => {
  try {
    syncLogger.log('开始同步数据...');
    
    // 获取本地数据
    const state = getStore().getState();
    const { navItems } = state.nav;
    const { websitesByNav } = state.website;
    
    // 整合本地数据
    const localData = integrateLocalData(navItems, websitesByNav);
    
    // 调用同步接口
    const mergedData = await callSyncApi(localData);
    
    if (!Array.isArray(mergedData)) {
      throw new Error('服务端返回的数据格式不正确');
    }
    
    // 分离合并后的数据
    const { navItems: updatedNavItems, websitesByNav: updatedWebsitesByNav } = separateMergedData(mergedData);
    
    // 更新本地数据
    dispatch(setNavItems(updatedNavItems));
    dispatch(setAllWebsites(updatedWebsitesByNav));
    
    syncLogger.log('同步完成');
    return { success: true, message: '同步成功' };
  } catch (error) {
    syncLogger.error('同步失败:', error);
    return { success: false, message: '同步失败: ' + error.message };
  }
};

/**
 * 重置所有数据到默认状态
 * @param {Function} dispatch Redux dispatch 函数
 * @returns {Promise<{success: boolean, message: string}>} 重置结果和消息
 */
export const resetAllData = async (dispatch) => {
  try {
    syncLogger.log('开始重置数据...');
    // 获取认证token
    const token = await getAuthToken();

    // 保存认证信息
    const authToken = storage.getAuthToken();
    const userInfo = storage.getUserInfo();
    
    // 清除所有数据
    storage.clear();
    
    // 恢复认证信息
    if (authToken) {
      storage.setAuthToken(authToken);
    }
    if (userInfo) {
      storage.setUserInfo(userInfo);
    }
    
    // 重置导航到首页
    storage.setLastSelectedNav('home');

    //已登陆
    if (token) {
      // 调用重置接口
      const response = await post('/navs/reset', null, token);
      const resetData = response.data;

      if (!Array.isArray(resetData)) {
        throw new Error('服务端返回的数据格式不正确');
      }
      // 分离重置后的数据
      const { navItems: resetNavItems, websitesByNav: resetWebsitesByNav } = separateMergedData(resetData);
      
      // 更新本地数据
      dispatch(setNavItems(resetNavItems));
    dispatch(setAllWebsites(resetWebsitesByNav));
    }
    // 只在确实同步成功或者有数据更新时触发页面刷新
    syncLogger.log('触发重置完成事件');
    syncLogger.log('重置完成');
    return { success: true, message: '重置成功' };
  } catch (error) {
    syncLogger.error('重置失败:', error);
    return { success: false, message: '重置失败: ' + error.message };
  }
};

export default syncAllDataToServer; 